import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/lib/i18n/LanguageContext";

/**
 * Opens the document's tag editor. With tags it is the compact "+" after them,
 * shown on the hovered row; with none it is an always-visible "Add tags" pill,
 * so an untagged document still offers a way to tag it by hand.
 */
export function DocumentTagEditorButton({
  empty,
  onClick,
}: {
  empty: boolean;
  onClick: () => void;
}) {
  const { t } = useLanguage();
  const label = empty ? t.documents.status.addTags : t.documents.status.editTags;
  const open = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };
  if (empty) {
    return (
      <button
        type="button"
        data-document-tag-editor="add"
        className="inline-flex h-6 flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-dashed border-border px-2 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={open}
      >
        <Plus className="h-3 w-3" aria-hidden="true" />
        {label}
      </button>
    );
  }
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            data-document-tag-editor="edit"
            // Shown on the hovered row or card only (and on keyboard focus), so
            // a list of tagged documents is not a column of "+" signs.
            className="h-7 w-7 flex-shrink-0 p-0 text-foreground opacity-0 transition-opacity hover:bg-neutral-200 focus-visible:opacity-100 group-hover/card:opacity-100 group-hover/row:opacity-100"
            onClick={open}
            aria-label={label}
          >
            <Plus className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top" className="z-50">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
